import PropTypes from "prop-types";
import React, { useState } from "react";
import moment from "moment";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import lround from "lodash/round";
import get from "lodash/get";
import EditorSessionTableRow from "./EditorSessionTableRow";
import submit from "submit-form";
import convert from "convert-units";
import ToolTip from "../../../../parts/tooltip-portal";
import {
  validateSet,
  checkOtherExercise,
} from "../../../../utils/exercise-validations";
import addMetadata from "../../../../utils/add-metadata";
import Sortable from "sortablejs";
import SvgIcon from "../../../../parts/SvgIcon";
import SaveInputButtons from "../../../../parts/SaveInputButtons";
import { CopyExerciseComponent } from "../../../../parts/CopyExerciseComponent";
import { DATE_FORMATS, PRIMARIESWITHOLYMPIC } from "../../../../constants";
import { AddExerciseProgram } from "./AddExerciseProgram";
import { PasteExerciseComponent } from "../../../../parts/PasteExerciseComponent";

const { DATE_DAY_FORMAT } = DATE_FORMATS;

class EditorSessionTable extends React.Component {
  constructor(props, ctx) {
    super(props, ctx);

    this.state = {
      openQuickEdit: false,
      cycle: ctx.locals.params.cycleId,
      isEditing: ctx.locals.query.edit,
      program: ctx.locals.query.program,
      isProgramEditor: this.props.isProgramEditor || false,
      exercises: [],
    };
    this.loadInputs = [];
    this.intensityInputs = [];

    this.updateExerciseByState = this.updateExerciseByState.bind(this);
    this.saveEdit = this.saveEdit.bind(this);
    this.saveQuickEdit = this.saveQuickEdit.bind(this);
    this.closeQuickEditMode = this.closeQuickEditMode.bind(this);
    this.openQuickEditMode = this.openQuickEditMode.bind(this);
    this.setEditing = this.setEditing.bind(this);
    this.loadSortable = this.loadSortable.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.isProgramEditor && !isEqual(nextState, this.state)) {
      return true;
    }
    if (
      this.props.isProgramEditor &&
      this.props.week.length !== nextProps.week.length
    ) {
      return true;
    }
    if (!isEqual(this.props.session, nextProps.session)) {
      return true;
    }

    if (
      !isEqual(this.props.exercisesInClipboard, nextProps.exercisesInClipboard)
    ) {
      return true;
    }

    if (
      this.props.isProgramEditor &&
      nextProps.opacity === this.props.opacity &&
      isEqual(nextProps.exerciseList, this.props.exerciseList)
    ) {
      return false;
    }
    return true;
  }

  componentDidMount() {
    // Fixes MSB-947
    setTimeout(() => {
      const hash = this.props.scrollTo;

      if (hash) {
        const elementToScroll = document.getElementById(hash);
        if (elementToScroll) {
          elementToScroll.scrollIntoView({ behavior: "smooth" });
        }
      }
    }, 1000);
    const { isEditing } = this.state;
    const {
      session: { week },
      sessionIndex,
    } = this.props;

    if (
      isEditing &&
      week === this.context.locals.query.weekIndex &&
      sessionIndex === this.context.locals.query.sessionIndex
    ) {
      this.setState({ openQuickEdit: true }, () => {
        setTimeout(() => {
          const elementToScroll = document.getElementById(
            this.context.locals.query.editExerciseId
          );

          elementToScroll.scrollIntoView(false);
        });
      });
    }
    const selector = `.exercises-list.workout-${this.props.session.order}-list.week-${this.props.session.week}`;
    const element = document.querySelector(selector);
    if (element) this.loadSortable(element);
  }

  loadSortable(element) {
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }
    const dragImage = document.createElement("img");
    dragImage.src =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
    this.sortable = Sortable.create(element, {
      group: "workout",
      animation: 350,
      handle: ".drag-icon",
      setData(dataTransfer) {
        dataTransfer.setDragImage(dragImage, 0, 0);
      },
      onEnd: (e) => {
        const { flash, editExercises } = this.context.locals;
        const { cycle, session, programOrder } = this.props;
        const { item, from, to, oldIndex, newIndex } = e;
        const fromWeek = from.getAttribute("data-week");
        const fromSession = from.getAttribute("data-session");
        const toWeek = to.getAttribute("data-week");
        const toSession = to.getAttribute("data-session");
        const sameList = fromWeek === toWeek && fromSession === toSession;

        if (oldIndex === newIndex && sameList) return;

        let withinSuperset = false;
        const exercises = [];
        const toExercises = [];

        const fromItems = from.querySelectorAll(":scope > li");
        const toItems = to.querySelectorAll(":scope > li");
        for (let i = 0; i < fromItems.length; i++) {
          const element = fromItems[i];
          const nextElement = fromItems[i + 1];
          const exercise = editExercises.find((exercise) => {
            return exercise._id == element.id;
          });

          if (
            exercise?.nextSuperset &&
            exercise.nextSuperset != nextElement?.id
          ) {
            withinSuperset = true;
          }
          exercises.push(element.id);
        }
        if (!sameList) {
          for (let i = 0; i < toItems.length; i++) {
            const element = toItems[i];
            const nextElement = toItems[i + 1];
            const exercise = editExercises.find((exercise) => {
              return exercise._id == element.id;
            });

            if (
              exercise?.nextSuperset &&
              exercise.nextSuperset != nextElement?.id
            ) {
              withinSuperset = true;
            }
            toExercises.push(element.id);
          }
          from.insertBefore(item, fromItems[oldIndex]);
        } else {
          from.insertBefore(item, fromItems[oldIndex + (oldIndex > newIndex)]);
        }

        let body = {
          cycle: cycle._id,
          program: programOrder,
          session: session.order,
          week: session.week,
          exercises,
        };

        if (!sameList) {
          body = { ...body, toExercises, toWeek, toSession };
        }

        if (withinSuperset) {
          flash("error", "Cannot reorder within a superset");
          submit("/actions/refresh");
        } else {
          submit("/actions/exercise/reorder", {
            method: "POST",
            body,
          });
        }
      },
    });
  }

  componentDidUpdate(prevProps) {
    const {
      exerciseList,
      session: { order, week },
    } = this.props;
    const selector = `.exercises-list.workout-${order}-list.week-${week}`;
    const element = document.querySelector(selector);
    if (element && !isEqual(exerciseList, prevProps.exerciseList)) {
      this.loadSortable(element);
    }
  }

  openQuickEditMode(e) {
    e.preventDefault();
    this.setState((prevState) =>
      Object.assign({}, prevState, { openQuickEdit: true })
    );
    this.props.workoutOpacityCallback({ type: "add", keyId: this.props.keyId });
  }

  closeQuickEditMode(e) {
    e && e.preventDefault();
    document.getElementById("quick-edit").reset();

    this.setState({ exercises: [], openQuickEdit: false, isEditing: false });
    this.props.workoutOpacityCallback({
      type: "remove",
      keyId: this.props.keyId,
    });
  }

  handleQuickEditChange(e, exId, setId) {
    let {
      target: { name, value },
    } = e;
    const { locals } = this.context;
    const exercises = cloneDeep(this.state.exercises);
    const exerciseList = cloneDeep(this.props.exerciseList);
    const exerciseIndex = exercises.findIndex(
      (ex) => ex._id.toString() == exId
    );
    const exerciseListIndex = exerciseList.findIndex(
      (ex) => ex._id.toString() == exId
    );
    const exercise =
      exercises[exerciseIndex] ||
      cloneDeep(get(this.context.locals, "editExercises", [])).find(
        (ex) => ex._id == exId
      ) ||
      cloneDeep(get(this.context.locals, "activeExercises", [])).find(
        (ex) => ex._id == exId
      ) ||
      {};

    const set = get(exercise, ["sets", setId], false);
    if (["load", "percentRepMax", "loadDrop"].includes(name)) {
      if (String(value).includes(",")) {
        value = e.target.value = String(value).replace(",", ".");
      }
    }

    if (set && set[name] !== value && name !== "exerciseType") {
      set[name] = value;
      const primary = get(exercise, "primary");
      const repMax =
        exercise.type != "secondary"
          ? get(
              exercise,
              `repMaximums.${primary}`,
              get(this.props.activeUser, `repMaximums.${primary}`, 0)
            )
          : exercise.secondaryRepMaximum;

      // ensure load and percentRepMax are in sync
      if (name === "load") {
        if (this.context.locals.activeUser.units === "imperial") {
          set.load = lround(convert(value).from("lb").to("kg"), 2);
        }
        if (exercise.type !== "assistant") {
          set.percentRepMax = lround((set.load / repMax) * 100, 1);
        }
      }

      if (name === "percentRepMax") {
        set.load = lround((repMax * set.percentRepMax) / 100, 2);
      }

      const loadInput = this.loadInputs[`${exId}_${setId}`];
      if (loadInput) {
        if (name === "percentRepMax") {
          let loadToShow = set.load;
          if (this.context.locals.activeUser.units === "imperial") {
            loadToShow = lround(convert(loadToShow).from("kg").to("lb"), 2);
          }
          loadInput.value = loadToShow;
        }
        if (!this.intensityInputs[`${exId}_${setId}`]) {
          this.intensityInputs[`${exId}_${setId}`] = { value: 0 };
        }
        this.intensityInputs[`${exId}_${setId}`].value = set.percentRepMax;
      }

      const validatedSet = validateSet({
        exercise,
        set,
        locals,
        field: name,
      });
      if (validatedSet) {
        exercise.sets[setId] = validatedSet;

        this.setExersicesStates(
          exercise,
          exercises,
          exerciseIndex,
          exerciseList,
          exerciseListIndex
        );
      } else {
        // revert to original value
        document.getElementById(`${exId}_${setId}_${name}`).value = cloneDeep(
          get(this.context.locals, "editExercises", [])
        ).find((ex) => ex._id == exId).sets[setId][name];
      }
    } else if (name === "notes") {
      // Have to add a space " " in the empty state "" because if not rill gets rid
      // of them when passing it to the backend (interprets it as false)
      exercise.notes = value === "" ? " " : value;

      this.setExersicesStates(
        exercise,
        exercises,
        exerciseIndex,
        exerciseList,
        exerciseListIndex
      );
    } else if (name === "exerciseType") {
      const { movement, repMaximum, newPrimary } = value;
      const sets = get(exercise, "sets", false);

      addMetadata(
        exercise,
        movement,
        newPrimary,
        this.context.locals.activeUserToRequest.primaryExercises,
        this.context.locals.activeUserToRequest.exerciseLibrary
      );
      // if the movement chosen is a primary, change to a primary
      if (PRIMARIESWITHOLYMPIC.includes(movement)) {
        // setting new values
        exercise.type = "primary";
        exercise.primary = movement;
        // resetting secondary and secondary RM
        exercise.secondary = "null";
        exercise.secondaryRepMaximum = 0;
        exercise.parameters = "null";
        // updating the values of each set in the exercise
        sets.map((currentSet, idx) => {
          if (
            currentSet.load &&
            currentSet.percentRepMax &&
            currentSet.load >= 0 &&
            currentSet.percentRepMax >= 0
          ) {
            currentSet.load = lround(
              (repMaximum * currentSet.percentRepMax) / 100,
              2
            );
            currentSet.percentRepMax = lround(
              (currentSet.load / repMaximum) * 100,
              1
            );
          }

          // updating the load shown
          const loadInput = this.loadInputs[`${exId}_${idx}`];
          if (loadInput) {
            let loadToShow = currentSet.load;
            const { units } = this.context.locals.activeUser;

            if (units === "imperial") {
              loadToShow = lround(convert(loadToShow).from("kg").to("lb"), 2);
            }
            loadInput.value = loadToShow;
          }
          // Making sure no values are invalid after any changes
          if (!currentSet.load || currentSet.load === null) currentSet.load = 0;
          if (!currentSet.percentRepMax || currentSet.percentRepMax === null)
            currentSet.percentRepMax = 0;
          if (!currentSet.sets) currentSet.sets = 1;
          if (!currentSet.reps) currentSet.reps = 1;
          if (!currentSet.load && !currentSet.rpe) currentSet.rpe = 5;
        });
      }
      // if repMaximum is undefined and isAssistant = true then it is assistant
      else if (!repMaximum && newPrimary === "assistant") {
        exercise.type = "assistant";
        exercise.primary = "assistant";
        exercise.secondary = movement;
        exercise.secondaryRepMaximum = 0;
        if (value.parameters) exercise.parameters = value.parameters;
      }
      // else its a secondary
      else {
        // change type from primary/assistant to secondary and set new values
        exercise.type = "secondary";
        exercise.primary = newPrimary;
        exercise.secondary = movement;
        exercise.secondaryRepMaximum = repMaximum;
        exercise.parameters = "null";
        // updating the values of each set in the exercise
        sets.map((currentSet, idx) => {
          if (
            currentSet.load &&
            currentSet.percentRepMax &&
            currentSet.load >= 0 &&
            currentSet.percentRepMax >= 0
          ) {
            currentSet.load = lround(
              (repMaximum * currentSet.percentRepMax) / 100,
              2
            );
            currentSet.percentRepMax = lround(
              (currentSet.load / repMaximum) * 100,
              1
            );
          }

          // updating the load shown
          const loadInput = this.loadInputs[`${exId}_${idx}`];
          if (loadInput) {
            let loadToShow = currentSet.load;
            const { units } = this.context.locals.activeUser;

            if (units === "imperial") {
              loadToShow = lround(convert(loadToShow).from("kg").to("lb"), 2);
            }
            loadInput.value = loadToShow;
          }
          // Making sure no values are invalid after any changes
          if (!currentSet.load || currentSet.load === null) currentSet.load = 0;
          if (!currentSet.percentRepMax || currentSet.percentRepMax === null)
            currentSet.percentRepMax = 0;
          if (!currentSet.sets) currentSet.sets = 1;
          if (!currentSet.reps) currentSet.reps = 1;
          if (!currentSet.load && !currentSet.rpe) currentSet.rpe = 5;
        });
      }

      this.setExersicesStates(
        exercise,
        exercises,
        exerciseIndex,
        exerciseList,
        exerciseListIndex,
        true
      );
    }
  }

  setExersicesStates(
    exercise,
    exercises,
    exerciseIndex,
    exerciseList,
    exerciseListIndex,
    saveAfterChange = false
  ) {
    // if exercise already exists in state
    if (exerciseIndex > -1) {
      exercises[exerciseIndex] = exercise;
      exerciseList[exerciseListIndex] = exercise;
    }
    // otherwise push to exercises
    if (exerciseIndex < 0) {
      exercises.push(exercise);
    }
    this.setState(
      { exercises },
      saveAfterChange ? () => this.saveEdit(false) : undefined
    );
  }

  saveEdit(close = true) {
    const { exercises } = this.state;

    if (
      exercises != undefined &&
      exercises.length > 0 &&
      checkOtherExercise(exercises)
    ) {
      if (this.props.isProgramEditor) {
        this.props.updateExercises(exercises);
      }

      let body = {
        exercises,
        omitModified: true,
        omitActiveUser: true,
      };

      const localsContext = this.context.locals;
      if (localsContext.activeUser.organizationId) {
        const organizationCoach = localsContext.activeUserToRequest._id;
        body = { ...body, organizationCoach };
      }

      submit(
        `/actions/exercise/quick-edit?proxyAthlete=${get(
          this.context.locals,
          "query.athlete"
        )}`,
        {
          method: "POST",
          body,
        }
      );
    }
    if (close) this.closeQuickEditMode();
  }

  saveQuickEdit(e) {
    e.preventDefault();
    this.saveEdit();
  }

  updateExerciseByState(exercise, index) {
    const copyExercises = [...this.state.exercises];
    copyExercises[index] = exercise;
    this.setState({ exercises: copyExercises }, this.saveEdit);
  }

  setEditing(isEditing = true) {
    this.setState({ isEditing });
  }

  render() {
    const {
      session,
      sessionIndex,
      week,
      fromMetric,
      activeUser,
      activeUserToRequest,
      cycle,
      program,
      programOrder,
      altClass,
      column,
      updateExercise,
      deleteExercise,
      updateOrderExercises,
      opacity,
    } = this.props;

    const order = session.order;
    const cycleId = cycle._id;
    const { openQuickEditMode } = this;
    const isUser =
      cycle.type == "user" ||
      cycle.type == "coach" ||
      cycle.type === "subscription";
    const activeUserToUpdateSessions =
      this.context.locals.athlete || this.context.locals.activeUser;
    const activeUserToHandleCopyPasteAction =
      this.context.locals.athlete || this.context.locals.activeUserToRequest;
    const isCoach =
      this.context.locals.pathname.includes("athlete-portal") ||
      this.context.locals.pathname.includes("coaching-portal");

    const highestOrder =
      !this.props.exerciseList || this.props.exerciseList?.length === 0
        ? 0
        : Math.max(...this.props.exerciseList.map(({ order }) => order ?? 0));

    const notSingleWorkout = cycle.storeType !== "single-workout";

    if (session?.exercises?.length > 0) {
      return (
        <li
          className="workout"
          key={`workout_${sessionIndex}_${column}`}
          style={{ opacity: opacity ? 0.3 : 1 }}
        >
          <header
            className={`${
              cycle.storeType === "single-workout" ? "!top-0" : ""
            }`}
          >
            {notSingleWorkout ? (
              <EditWorkoutNameComponent
                cycleId={cycleId}
                session={session}
                order={order}
                programNumber={this.state.program}
                activeUserToUpdateSessions={activeUserToUpdateSessions}
                showCopyButton={true}
                isCoach={isCoach}
                activeUserToHandleCopyPasteAction={
                  activeUserToHandleCopyPasteAction
                }
                exerciseList={this.props.exerciseList}
              />
            ) : (
              <p className="s2">Single Workout</p>
            )}
          </header>
          <div
            className={`exercises-frame ${
              this.state.openQuickEdit && "quick-edit"
            }`}
          >
            <form id="quick-edit">
              <ul
                className={`exercises-list workout-${order}-list week-${session.week}`}
                ref="exercises"
                key={cycleId}
                data-week={session.week}
                data-session={order}
              >
                {this.props.exerciseList.map((exercise, i) => {
                  return (
                    <EditorSessionTableRow
                      key={exercise._id}
                      isProgramEditor={this.state.isProgramEditor}
                      exercises={this.props.exerciseList}
                      exercise={exercise._id}
                      exerciseOrder={i}
                      fromMetric={fromMetric}
                      activeUser={activeUser}
                      cycle={cycle}
                      program={program}
                      programOrder={programOrder}
                      session={session}
                      sessionIndex={sessionIndex}
                      altClass={altClass}
                      openQuickEditMode={openQuickEditMode}
                      handleQuickEditChange={(e, exId, setId) =>
                        this.handleQuickEditChange(e, exId, setId)
                      }
                      loadInputs={this.loadInputs}
                      intensityInputs={this.intensityInputs}
                      isQuickEditOpen={this.state.openQuickEdit}
                      updateExercise={updateExercise}
                      deleteExercise={deleteExercise}
                      updateOrderExercises={updateOrderExercises}
                      exercisesInState={this.state.exercises}
                      updateExerciseByState={this.updateExerciseByState}
                      setEditing={this.setEditing}
                      stateExercises={this.state.exercises}
                      isCoach={isCoach}
                    />
                  );
                })}

                {/* <EditorSessionTableRow /> */}
              </ul>
            </form>
            <footer
              className={
                this.state.openQuickEdit ? "justify-end" : "justify-between"
              }
            >
              {this.state.openQuickEdit ? (
                <>
                  <button
                    onClick={this.closeQuickEditMode}
                    data-button-type="secondary"
                    className="!text-[--danger] discard"
                  >
                    Discard
                  </button>
                  <button
                    onClick={this.saveQuickEdit}
                    data-button-type="primary"
                  >
                    Save
                  </button>
                </>
              ) : (
                <>
                  <a
                    data-button-type="text"
                    role="button"
                    href={`/actions/refresh?modal[type]=add-exercise&programWeek=${
                      session.week
                    }&programSession=${sessionIndex + 1}&editMode=false`}
                    className="pl-1"
                  >
                    <SvgIcon
                      src="/img/new-icons/add-gradient-icon.svg"
                      className="h-5 w-5"
                      noFilterOnLight
                    />
                    <span className="font-semibold">Add Exercise</span>
                  </a>
                  <div className="flex items-center gap-1">
                    <PasteExerciseComponent
                      athlete={activeUserToHandleCopyPasteAction}
                      date={moment().format(DATE_DAY_FORMAT)}
                      query={this.props.exercisesInClipboard}
                      haveExercises={true}
                      isProgram={true}
                      cycle={cycle}
                      sessionProgram={session}
                      programNumber={this.state.program}
                      exercisesLength={this.props.exerciseList?.length}
                      highestOrder={highestOrder}
                    />

                    {notSingleWorkout && (
                      <ToolTip body="Workout Progression">
                        <a
                          href={`/actions/refresh?modal[type]=progression&type=workout&week=${
                            session.week
                          }&session=${
                            sessionIndex + 1
                          }&program=${programOrder}&cycle=${cycleId}`}
                          data-button-type="secondary"
                          role="button"
                        >
                          <SvgIcon src="/img/new-icons/percentage-square.svg" />
                        </a>
                      </ToolTip>
                    )}

                    <ToolTip
                      body="Quick Edit"
                      childrenContainerClass="quick-edit"
                    >
                      <button
                        onClick={this.openQuickEditMode}
                        data-button-type="secondary"
                      >
                        <SvgIcon src="/img/new-icons/edit-pencil.svg" />
                      </button>
                    </ToolTip>

                    {notSingleWorkout && (
                      <ToolTip body="Duplicate Workout">
                        <a
                          href={`/actions/refresh?modal[type]=duplicate-workout&week=${
                            session.week
                          }&session=${sessionIndex + 1}&isExercise=${false}`}
                          data-button-type="secondary"
                          role="button"
                        >
                          <SvgIcon src="/img/new-icons/duplicate.svg" />
                        </a>
                      </ToolTip>
                    )}

                    {notSingleWorkout && (
                      <ToolTip body="Move Down">
                        <a
                          href={`/actions/cycle/reorder-workout/${cycleId}/${programOrder}/${
                            session.week
                          }/${order}/${order + 1}`}
                          data-button-type="secondary"
                          role="button"
                          aria-disabled={sessionIndex === week.length - 1}
                        >
                          <SvgIcon src="/img/new-icons/arrow-square-down.svg" />
                        </a>
                      </ToolTip>
                    )}

                    {notSingleWorkout && (
                      <ToolTip body="Move Up">
                        <a
                          href={`/actions/cycle/reorder-workout/${cycleId}/${programOrder}/${
                            session.week
                          }/${order}/${order - 1}`}
                          data-button-type="secondary"
                          role="button"
                          aria-disabled={sessionIndex === 0}
                        >
                          <SvgIcon src="/img/new-icons/arrow-square-up.svg" />
                        </a>
                      </ToolTip>
                    )}

                    {isUser && week.length > 1 && notSingleWorkout && (
                      <ToolTip body="Delete Workout">
                        <a
                          href={`/actions/cycle/delete-workout/${cycleId}/${programOrder}/${session.week}/${order}`}
                          data-button-type="secondary"
                          role="button"
                        >
                          <SvgIcon
                            src="/img/new-icons/red-trash.svg"
                            noFilterOnLight
                          />
                        </a>
                      </ToolTip>
                    )}

                    {isUser && week.length === 1 && notSingleWorkout && (
                      <ToolTip body="Delete Week">
                        <a
                          href={`/actions/cycle/delete-week/${cycleId}/${programOrder}/${session.week}`}
                          data-button-type="secondary"
                          role="button"
                        >
                          <SvgIcon
                            src="/img/new-icons/red-trash.svg"
                            noFilterOnLight
                          />
                        </a>
                      </ToolTip>
                    )}

                    {isUser && week.length === 1 && !notSingleWorkout && (
                      <ToolTip body="Clear workout">
                        <a
                          href={`/actions/cycle/delete-single-workout/${cycleId}/`}
                          data-button-type="secondary"
                          role="button"
                        >
                          <SvgIcon
                            src="/img/new-icons/red-trash.svg"
                            noFilterOnLight
                          />
                        </a>
                      </ToolTip>
                    )}
                  </div>
                </>
              )}
            </footer>
          </div>
        </li>
      );
    }
    return (
      <li className="workout alt-add">
        <header>
          {notSingleWorkout ? (
            <EditWorkoutNameComponent
              cycleId={cycleId}
              session={session}
              order={order}
              programNumber={this.state.program}
              activeUserToUpdateSessions={activeUserToUpdateSessions}
              showCopyButton={false}
              isCoach={isCoach}
              activeUserToHandleCopyPasteAction={
                activeUserToHandleCopyPasteAction
              }
              exerciseList={this.props.exerciseList}
            />
          ) : (
            <p className="s2">Single Workout</p>
          )}
        </header>
        <ul
          className={`exercises-list empty workout-${order}-list week-${session.week}`}
          ref="exercises"
          key={cycleId}
          data-week={session.week}
          data-session={order}
          style={{ minHeight: 0, height: 0 }}
        />
        <div className={`exercises-frame alt-add workout-${order}-list`}>
          <AddExerciseProgram
            session={session}
            sessionIndex={sessionIndex}
            activeUserToHandleCopyPasteAction={
              activeUserToHandleCopyPasteAction
            }
            program={this.state.program}
            cycle={cycle}
            exercisesInClipboard={this.props.exercisesInClipboard}
            date={moment().format(DATE_DAY_FORMAT)}
          />
        </div>
        <footer className="justify-end">
          {notSingleWorkout && (
            <ToolTip
              id={`move-next-${sessionIndex}-${session.week}`}
              body="Move Down"
            >
              <a
                data-button-type="secondary"
                role="button"
                href={`/actions/cycle/reorder-workout/${cycleId}/${programOrder}/${
                  session.week
                }/${order}/${order + 1}`}
                aria-disabled={sessionIndex === week.length - 1}
              >
                <SvgIcon src="/img/new-icons/arrow-square-down.svg" />
              </a>
            </ToolTip>
          )}

          {notSingleWorkout && (
            <ToolTip
              id={`move-prev-${sessionIndex}-${session.week}`}
              body="Move Up"
            >
              <a
                data-button-type="secondary"
                role="button"
                href={`/actions/cycle/reorder-workout/${cycleId}/${programOrder}/${
                  session.week
                }/${order}/${order - 1}`}
                aria-disabled={sessionIndex === 0}
              >
                <SvgIcon src="/img/new-icons/arrow-square-up.svg" />
              </a>
            </ToolTip>
          )}

          {isUser && week.length > 1 && (
            <ToolTip
              id={`delete-workout-${sessionIndex}-${session.week}`}
              body="Delete Workout"
            >
              <a
                data-button-type="secondary"
                role="button"
                href={`/actions/cycle/delete-workout/${cycleId}/${programOrder}/${session.week}/${order}`}
              >
                <SvgIcon src="/img/new-icons/red-trash.svg" noFilterOnLight />
              </a>
            </ToolTip>
          )}
          {isUser && week.length === 1 && (
            <ToolTip
              id={`delete-week-${sessionIndex}-${session.week}`}
              body="Delete Week"
            >
              <a
                data-button-type="secondary"
                role="button"
                href={`/actions/cycle/delete-week/${cycleId}/${programOrder}/${session.week}`}
                aria-disabled={!notSingleWorkout}
              >
                <SvgIcon src="/img/new-icons/red-trash.svg" noFilterOnLight />
              </a>
            </ToolTip>
          )}
        </footer>
      </li>
    );
  }
}

EditorSessionTable.contextTypes = { locals: PropTypes.object };

export default EditorSessionTable;

/**
 * Render the edit workout name component
 * @param {*} param0
 * @returns
 */

const EditWorkoutNameComponent = ({
  cycleId,
  session,
  order,
  programNumber,
  activeUserToUpdateSessions,
  showCopyButton,
  isCoach,
  activeUserToHandleCopyPasteAction,
  exerciseList,
}) => {
  const workoutBaseName = `Workout ${order}`;
  const initialEditableName = session.workoutName
    ? String(session.workoutName).replace(`${workoutBaseName} - `, "")
    : "";
  const [isEditing, setIsEditing] = useState(false);
  const [editableWorkoutName, setEditableWorkoutName] =
    useState(initialEditableName);

  /**
   * function to assign the value of the input to the state
   */
  const handleInputWorkoutNameChange = ({ target: { value } }) => {
    setEditableWorkoutName(value);
  };

  /**
   * Submit function
   * @param {*} e
   */
  const handleEditWorkoutName = (e) => {
    e.preventDefault();

    const updatedWorkoutName =
      editableWorkoutName.length > 0 ? `${editableWorkoutName}`.trim() : "";

    const body = {
      updatedWorkoutName,
    };

    submit(
      `/actions/cycle/edit/program-workout-name/${cycleId}?program=${programNumber}&athlete=${activeUserToUpdateSessions}&sessionOrder=${session.order}&sessionWeek=${session.week}`,
      {
        method: "POST",
        body,
      }
    );

    setIsEditing(false);
  };

  return (
    <>
      {!isEditing ? (
        <>
          <p className="s2">
            {editableWorkoutName
              ? `${workoutBaseName} - ${editableWorkoutName}`.trim()
              : workoutBaseName}
          </p>
          <div className="flex items-center gap-1">
            {showCopyButton && isCoach && (
              <ToolTip body="Copy workout">
                <CopyExerciseComponent
                  exercises={exerciseList}
                  athlete={activeUserToHandleCopyPasteAction}
                />
              </ToolTip>
            )}
            <ToolTip body="Edit workout name">
              <button
                data-button-type="secondary"
                type="button"
                onClick={() => {
                  setIsEditing(true);
                }}
              >
                <SvgIcon src="/img/new-icons/edit-pencil.svg" />
              </button>
            </ToolTip>
          </div>
        </>
      ) : (
        <div className="workout-name-input-div">
          <p className="s2">{workoutBaseName}</p>
          {session.workoutName && <p className="s2"> - </p>}
          <input
            name="workoutName"
            placeholder={initialEditableName}
            value={editableWorkoutName}
            onChange={handleInputWorkoutNameChange}
          />
          <SaveInputButtons
            enabled={isEditing}
            handleSave={handleEditWorkoutName}
            handleDismiss={(e) => {
              e.preventDefault();
              setIsEditing(false);
              setEditableWorkoutName(initialEditableName);
            }}
            saveDisabled={editableWorkoutName === initialEditableName}
          />
        </div>
      )}
    </>
  );
};
